import React, { useEffect, useMemo, useState } from 'react';
import Box from 'apollo-react/components/Box';
import Paper from 'apollo-react/components/Paper';
import CircularProgress from 'apollo-react/components/CircularProgress';
import Typography from 'apollo-react/components/Typography';
import { useTranslation } from 'react-i18next';
import {
  useHcpProfileModule,
  sanitizeProfileBundle,
  profileBundleWithoutAttachmentRevisions,
} from '../../../../modules/hcpProfile/useHcpProfileModule';
import GeneralInformationTab from '../../../../modules/hcpProfile/tabs/GeneralInformationTab';
import LicenseInformationTab from '../../../../modules/hcpProfile/tabs/LicenseInformationTab';
import EducationExperienceTab from '../../../../modules/hcpProfile/tabs/EducationExperienceTab';
import styles from '../../../../modules/customerProfile/profileUi/Profile.module.css';
import { HCP_PROFILE_REQUEST_TAB_IDS } from '../../../../constants/hcpProfileRequestConstants';

export { HCP_PROFILE_REQUEST_TAB_IDS };

/** Main-menu entries for embedded HCP profile on requests (always full profile: personal, address, license, education). */
export function useHcpProfileRequestMainMenuTabs() {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        id: HCP_PROFILE_REQUEST_TAB_IDS.personal,
        name: t('requestFlow.hcpProfileTabPersonal', {
          defaultValue: 'Personal Information',
        }),
      },
      {
        id: HCP_PROFILE_REQUEST_TAB_IDS.address,
        name: t('requestFlow.hcpProfileTabAddress', {
          defaultValue: 'Address Information',
        }),
      },
      {
        id: HCP_PROFILE_REQUEST_TAB_IDS.license,
        name: t('requestFlow.hcpProfileTabLicense', {
          defaultValue: 'License Information',
        }),
      },
      {
        id: HCP_PROFILE_REQUEST_TAB_IDS.education,
        name: t('requestFlow.hcpProfileTabEducation', {
          defaultValue: 'Education and Experience',
        }),
      },
    ],
    [t]
  );
}

/**
 * Right-hand content for an HCP profile Main Menu tab (FormSections left rail).
 * - **live**: inline edit against `/me` (new request / send-back).
 * - **snapshot**: read-only bundle from `requestProfileSnapshotJson` (reviewers / completed).
 */
export default function EmbeddedHcpProfileForRequest({
  activeMainMenuTabId,
  profileDataMode = 'live',
  /** JSON string or object; used when `profileDataMode === 'snapshot'`. */
  requestProfileSnapshotJson = null,
  /**
   * When true with an attached profile snapshot, hide superseded file / prior-version panels
   * (e.g. admin reviewing an external HCP-submitted request).
   */
  hideAttachmentRevisionHistory = true,
  /**
   * Admin reviewers: hide snapshot banner text and applicant Add/Edit/Delete controls
   * (approvals + section comments live in FormSections).
   */
  suppressApplicantActionChrome = false,
}) {
  const { t } = useTranslation();
  const {
    profileData: liveData,
    loading: liveLoading,
    errorMessage: liveError,
    refreshProfile,
  } = useHcpProfileModule({ enabled: profileDataMode === 'live' });

  const snapshotBundle = useMemo(() => {
    if (profileDataMode !== 'snapshot') return null;
    if (
      requestProfileSnapshotJson == null ||
      requestProfileSnapshotJson === ''
    ) {
      return null;
    }
    try {
      const raw =
        typeof requestProfileSnapshotJson === 'string'
          ? JSON.parse(requestProfileSnapshotJson)
          : requestProfileSnapshotJson;
      return sanitizeProfileBundle(raw);
    } catch {
      return null;
    }
  }, [profileDataMode, requestProfileSnapshotJson]);

  const rawProfileData =
    profileDataMode === 'snapshot' ? snapshotBundle : liveData;
  const profileData =
    rawProfileData && hideAttachmentRevisionHistory
      ? profileBundleWithoutAttachmentRevisions(rawProfileData)
      : rawProfileData;
  const loading = profileDataMode === 'snapshot' ? false : liveLoading;
  const errorMessage =
    profileDataMode === 'snapshot'
      ? snapshotBundle
        ? null
        : t('requestFlow.hcpProfileSnapshotMissing', {
            defaultValue: 'No profile snapshot on this request.',
          })
      : liveError;

  const [mountedTabs, setMountedTabs] = useState(() => new Set());

  const tabKeyForId = useMemo(() => {
    const m = new Map([
      [HCP_PROFILE_REQUEST_TAB_IDS.personal, 'personal'],
      [HCP_PROFILE_REQUEST_TAB_IDS.address, 'address'],
      [HCP_PROFILE_REQUEST_TAB_IDS.license, 'license'],
      [HCP_PROFILE_REQUEST_TAB_IDS.education, 'education'],
    ]);
    return (id) => m.get(id) ?? null;
  }, []);

  const activeKey = activeMainMenuTabId
    ? tabKeyForId(activeMainMenuTabId)
    : null;

  useEffect(() => {
    if (!activeKey) {
      return;
    }
    setMountedTabs((prev) => new Set([...prev, activeKey]));
  }, [activeKey]);

  const isSnapshotView = profileDataMode === 'snapshot';
  const liveLoadingState = loading && !profileData;

  if (!activeMainMenuTabId || !activeKey) {
    return null;
  }

  if (liveLoadingState) {
    return (
      <Box textAlign="center" py={4}>
        <CircularProgress />
        <Typography variant="body" color="text.secondary" sx={{ mt: 2 }}>
          {t('loading', { defaultValue: 'Loading…' })}
        </Typography>
      </Box>
    );
  }

  if (errorMessage && !profileData) {
    return (
      <Box sx={{ mt: 2, width: '100%' }}>
        <Typography variant="body2">{errorMessage}</Typography>
      </Box>
    );
  }

  if (!profileData) {
    return (
      <Box sx={{ mt: 2, width: '100%' }}>
        <Typography variant="body2">
          {t('profileData.loadError', {
            defaultValue: 'Could not load your profile.',
          })}
        </Typography>
      </Box>
    );
  }

  const data = profileData;

  const snapshotReadOnly = profileDataMode === 'snapshot';
  const showAttachmentPriorVersions = !hideAttachmentRevisionHistory;

  const tabPropsBase = {
    segment: data.general,
    lockedPaths: data.lockedGeneralPaths,
    lockMessages: data.generalLockMessages,
    verificationLockedPaths:
      data.profileFieldPermissions?.verificationLockedPaths,
    validationConfig: data.validationConfig,
    attachmentRevisions: data.attachmentRevisions || [],
    onSaved: isSnapshotView ? undefined : refreshProfile,
    showTitle: false,
    cardFieldLayout: true,
    embedInlineEdit: !isSnapshotView,
    embedInlineAutosave: !isSnapshotView,
    readOnly: snapshotReadOnly,
    showAttachmentPriorVersions,
  };

  const personalContent = (
    <div className={styles.profileFieldWidth}>
      <GeneralInformationTab variant="personal" {...tabPropsBase} />
    </div>
  );

  const addressContent = (
    <div className={styles.profileFieldWidth}>
      <GeneralInformationTab variant="address" {...tabPropsBase} />
    </div>
  );

  const licensesContent = (
    <div className={styles.profileFieldWidth}>
      <LicenseInformationTab
        licenses={data.licenses}
        licenseFieldConfig={data.licenseFieldConfig}
        licenseRepeatableLimits={data.licenseRepeatableLimits}
        attachmentRevisions={data.attachmentRevisions || []}
        onSaved={isSnapshotView ? undefined : refreshProfile}
        readOnly={isSnapshotView}
        showAttachmentPriorVersions={showAttachmentPriorVersions}
        suppressApplicantActionChrome={suppressApplicantActionChrome}
      />
    </div>
  );

  const educationContent = (
    <div className={styles.profileFieldWidth}>
      <EducationExperienceTab
        educations={data.educations}
        experience={data.experience}
        educationRepeatableLimits={data.educationRepeatableLimits}
        attachmentRevisions={data.attachmentRevisions || []}
        onSaved={isSnapshotView ? undefined : refreshProfile}
        showPageTitle={false}
        readOnly={isSnapshotView}
        showAttachmentPriorVersions={showAttachmentPriorVersions}
        suppressApplicantActionChrome={suppressApplicantActionChrome}
      />
    </div>
  );

  const panels = {
    personal: personalContent,
    address: addressContent,
    license: licensesContent,
    education: educationContent,
  };

  return (
    <Box sx={{ width: '100%' }}>
      {!isSnapshotView ? (
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 1,
            backgroundColor: '#ffffff',
            border: '0px',
          }}
        >
          <Typography>
            {t('requestFlow.hcpProfileSnapshotHelp', {
              defaultValue: '',
            })}
          </Typography>
        </Box>
      ) : !suppressApplicantActionChrome ? (
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 1,
            backgroundColor: '#ffffff',
            border: '0px',
          }}
        >
          <Typography variant="body2" emphasis="high">
            {t('requestFlow.hcpProfileAttachedReadOnly', {
              defaultValue: '',
            })}
          </Typography>
        </Box>
      ) : null}

      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: '1px solid #e0e4e8',
          borderRadius: 1,
          backgroundColor: '#fff',
        }}
      >
        {Object.keys(panels).map((key) => {
          const show = activeKey === key;
          if (!show && !mountedTabs.has(key)) {
            return null;
          }
          return (
            <div
              key={key}
              style={{ display: show ? 'block' : 'none' }}
              aria-hidden={!show}
            >
              {panels[key]}
            </div>
          );
        })}
      </Paper>
    </Box>
  );
}
